import { useCallback, useEffect, useRef, useState } from 'react';

interface PullToRefreshOptions {
  onRefresh: () => Promise<void> | void;
  pullDownThreshold?: number;
  maxPullDistance?: number;
  disabled?: boolean;
}

interface PullToRefreshState {
  isPulling: boolean;
  isRefreshing: boolean;
  pullDistance: number;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

export function usePullToRefresh({
  onRefresh,
  pullDownThreshold = 80,
  maxPullDistance = 140,
  disabled = false,
}: PullToRefreshOptions): PullToRefreshState {
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const startY = useRef(0);
  const currentY = useRef(0);
  const pulling = useRef(false);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (disabled || isRefreshing) return;

      // Only track when scrolled to the very top
      if (window.scrollY > 0 && document.documentElement.scrollTop > 0) return;

      startY.current = e.touches[0].clientY;
      pulling.current = true;
    },
    [disabled, isRefreshing]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!pulling.current || disabled || isRefreshing) return;

      currentY.current = e.touches[0].clientY;
      const diff = currentY.current - startY.current;

      // Only track downward pulls
      if (diff <= 0) {
        setIsPulling(false);
        setPullDistance(0);
        return;
      }

      // Don't prevent scroll when not at top
      if (window.scrollY > 0) {
        pulling.current = false;
        setIsPulling(false);
        setPullDistance(0);
        return;
      }

      // Apply resistance — the further you pull, the harder it gets
      const resistance = 0.4;
      const distance = Math.min(diff * resistance, maxPullDistance);

      setIsPulling(true);
      setPullDistance(distance);
    },
    [disabled, isRefreshing, maxPullDistance]
  );

  const handleTouchEnd = useCallback(async () => {
    if (!pulling.current) return;
    pulling.current = false;

    const diff = currentY.current - startY.current;
    const effectiveDistance = diff * 0.4;

    if (effectiveDistance >= pullDownThreshold && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(60); // Keep indicator visible during refresh

      try {
        await onRefresh();
      } catch {
        // Refresh failed, still reset
      }

      // Brief hold so user sees the checkmark
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    setIsRefreshing(false);
    setIsPulling(false);
    setPullDistance(0);
  }, [pullDownThreshold, isRefreshing, onRefresh]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return {
    isPulling,
    isRefreshing,
    pullDistance,
    containerRef,
  };
}